import type { ReactNode } from "react";
import { marked, type Token, type Tokens } from "marked";
import { Anchor, Blockquote, Box, Code, Divider, List, Table, Text, Title } from "@mantine/core";

/**
 * Validates that a link or image URL is safe to use in the DOM.
 * Only HTTP, HTTPS, mailto, and clean relative paths are admitted.
 * Scripts, data URIs, and dangerous schemes are rejected.
 */
function stripControlChars(str: string): string {
  let result = "";
  for (let i = 0; i < str.length; i++) {
    const code = str.charCodeAt(i);
    if ((code >= 0 && code <= 31) || (code >= 127 && code <= 159)) {
      continue;
    }
    result += str[i];
  }
  return result;
}

export function isSafeUrl(url: string): boolean {
  if (!url) return false;
  let decoded = url;
  try {
    decoded = decodeURIComponent(url);
  } catch {
    // If malformed URI sequence, check raw
  }
  const sanitized = stripControlChars(decoded).replace(/\s+/g, "").toLowerCase();
  if (
    sanitized.startsWith("javascript:") ||
    sanitized.startsWith("data:") ||
    sanitized.startsWith("vbscript:")
  ) {
    return false;
  }
  const clean = stripControlChars(url).trim();
  if (clean.startsWith("#")) return true;
  if (clean.startsWith("/") && !clean.startsWith("//")) return true;
  try {
    const parsed = new URL(clean);
    return (
      parsed.protocol === "http:" || parsed.protocol === "https:" || parsed.protocol === "mailto:"
    );
  } catch {
    return false;
  }
}

function renderToken(token: Token, index: number): ReactNode {
  switch (token.type) {
    case "heading": {
      const order = Math.min(Math.max(token.depth, 1), 6) as 1 | 2 | 3 | 4 | 5 | 6;
      return (
        <Title key={index} order={order} my="xs">
          {renderTokens(token.tokens)}
        </Title>
      );
    }
    case "paragraph":
      return (
        <Text key={index} component="p" mb="sm" lh={1.6}>
          {renderTokens(token.tokens)}
        </Text>
      );
    case "text":
      if ("tokens" in token && token.tokens && token.tokens.length > 0) {
        return <span key={index}>{renderTokens(token.tokens)}</span>;
      }
      return token.text;
    case "strong":
      return <strong key={index}>{renderTokens(token.tokens)}</strong>;
    case "em":
      return <em key={index}>{renderTokens(token.tokens)}</em>;
    case "del":
      return <s key={index}>{renderTokens(token.tokens)}</s>;
    case "codespan":
      return <Code key={index}>{token.text}</Code>;
    case "code":
      return (
        <Code key={index} block my="sm" style={{ overflowX: "auto" }}>
          {token.text}
        </Code>
      );
    case "blockquote":
      return (
        <Blockquote key={index} my="sm">
          {renderTokens(token.tokens)}
        </Blockquote>
      );
    case "list":
      return (
        <List key={index} type={token.ordered ? "ordered" : "unordered"} withPadding mb="sm">
          {token.items.map((item: Tokens.ListItem, itemIdx: number) => (
            <List.Item key={itemIdx}>{renderTokens(item.tokens)}</List.Item>
          ))}
        </List>
      );
    case "list_item":
      return <span key={index}>{renderTokens(token.tokens)}</span>;
    case "hr":
      return <Divider key={index} my="md" />;
    case "link": {
      const safe = isSafeUrl(token.href);
      if (!safe) {
        return <span key={index}>{renderTokens(token.tokens)}</span>;
      }
      const isExternal = token.href.startsWith("http://") || token.href.startsWith("https://");
      return (
        <Anchor
          key={index}
          href={token.href}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          title={token.title ?? undefined}
        >
          {renderTokens(token.tokens)}
        </Anchor>
      );
    }
    case "image": {
      const safe = isSafeUrl(token.href);
      const isAllowedProtocol =
        token.href.startsWith("http://") ||
        token.href.startsWith("https://") ||
        (token.href.startsWith("/") && !token.href.startsWith("//"));
      if (!safe || !isAllowedProtocol) {
        return <span key={index}>[image: {token.text}]</span>;
      }
      return (
        <img
          key={index}
          src={token.href}
          alt={token.text || ""}
          title={token.title ?? undefined}
          style={{
            maxWidth: "100%",
            height: "auto",
            borderRadius: "var(--mantine-radius-sm)",
          }}
        />
      );
    }
    case "table":
      return (
        <Box key={index} style={{ overflowX: "auto" }} my="sm">
          <Table striped withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                {token.header.map((cell: Tokens.TableCell, cellIdx: number) => (
                  <Table.Th key={cellIdx}>{renderTokens(cell.tokens)}</Table.Th>
                ))}
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {token.rows.map((row: Tokens.TableCell[], rowIdx: number) => (
                <Table.Tr key={rowIdx}>
                  {row.map((cell: Tokens.TableCell, cellIdx: number) => (
                    <Table.Td key={cellIdx}>{renderTokens(cell.tokens)}</Table.Td>
                  ))}
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Box>
      );
    case "html":
      // Never render raw executable HTML; JSX text node escapes all contents.
      return (
        <Text key={index} component="span">
          {token.text}
        </Text>
      );
    case "space":
      return null;
    default: {
      const candidate = token as unknown;
      if (typeof candidate === "object" && candidate !== null) {
        if ("tokens" in candidate && Array.isArray(candidate.tokens)) {
          return <span key={index}>{renderTokens(candidate.tokens as Token[])}</span>;
        }
        if ("text" in candidate && typeof candidate.text === "string") {
          return candidate.text;
        }
      }
      return null;
    }
  }
}

export function renderTokens(tokens?: Token[]): ReactNode {
  if (!tokens || tokens.length === 0) return null;
  return tokens.map((token, index) => renderToken(token, index));
}

/**
 * Shared safe Markdown renderer for public news posts and admin preview.
 * Tokens are rendered strictly as native React elements with automatic text escaping;
 * executable HTML tags and unsafe URI schemes (javascript:, data:) are never evaluated.
 */
export function MarkdownContent({ markdown }: { markdown: string }) {
  if (!markdown) return null;
  const tokens = marked.lexer(markdown);
  return <Box>{renderTokens(tokens)}</Box>;
}
